import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Row,
  Col,
  Tag,
  Button,
  Space,
  DatePicker,
  Typography,
  Tooltip,
  Progress,
  Alert,
} from 'antd';
import {
  TrophyOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { hasPermission } from '../../utils/permissions';
import {
  fetchAllVendorPerformanceRequest,
} from '../../redux/actions/vendorPerformanceActions';
import VendorPerformanceDetail, { getGradeTag } from './VendorPerformanceDetail';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

const fmtPct = (v) => (v != null ? `${Number(v).toFixed(1)}%` : '—');

const getScoreColor = (score) => {
  if (score >= 90) return '#52c41a';
  if (score >= 75) return '#1890ff';
  if (score >= 60) return '#faad14';
  if (score >= 45) return '#fa8c16';
  return '#ff4d4f';
};

const ScoreBar = ({ value }) => (
  <div style={{ minWidth: 100 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
      <Text strong style={{ fontSize: 13, color: getScoreColor(value) }}>
        {Number(value).toFixed(1)}
      </Text>
      {getGradeTag(
        value >= 90 ? 'A' : value >= 75 ? 'B' : value >= 60 ? 'C' : value >= 45 ? 'D' : 'F'
      )}
    </div>
    <Progress
      percent={value}
      showInfo={false}
      size="small"
      strokeColor={getScoreColor(value)}
      trailColor="#f0f0f0"
      style={{ marginBottom: 0 }}
    />
  </div>
);

const RateCell = ({ value, lowerIsBetter = false }) => {
  const v = Number(value) || 0;
  let color;
  if (lowerIsBetter) {
    color = v <= 5 ? '#52c41a' : v <= 15 ? '#faad14' : '#ff4d4f';
  } else {
    color = getScoreColor(v);
  }
  return <Text style={{ color, fontWeight: 500 }}>{fmtPct(v)}</Text>;
};

const VendorPerformance = () => {
  const dispatch = useDispatch();
  const permissions = useSelector((s) => s.auth.permissions || []);
  const { leaderboard, loading } = useSelector((s) => s.vendorPerformance);

  const canView = hasPermission(permissions, 'purchase_order/view');

  const [dateRange, setDateRange] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const loadLeaderboard = useCallback(() => {
    const params = {};
    if (dateRange?.[0]) params.date_from = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) params.date_to   = dateRange[1].format('YYYY-MM-DD');
    dispatch(fetchAllVendorPerformanceRequest(params));
  }, [dispatch, dateRange]);

  useEffect(() => {
    if (canView) loadLeaderboard();
  }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: 60, color: '#8c8c8c' }}>
          You don't have permission to view vendor performance.
        </div>
      </div>
    );
  }

  const vendors = leaderboard?.vendors || [];

  // Summary stats
  const avgScore = vendors.length
    ? vendors.reduce((s, v) => s + v.overall_score, 0) / vendors.length
    : 0;
  const topVendor = vendors[0];
  const gradeCounts = vendors.reduce((acc, v) => {
    acc[v.score_grade] = (acc[v.score_grade] || 0) + 1;
    return acc;
  }, {});

  const columns = [
    {
      title: 'Rank',
      key: 'rank',
      width: 60,
      render: (_, __, i) => {
        const medal = ['🥇', '🥈', '🥉'][i];
        return medal ? (
          <span style={{ fontSize: 18 }}>{medal}</span>
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>#{i + 1}</Text>
        );
      },
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor_name',
      key: 'vendor_name',
      render: (name, record) => (
        <div>
          <Text strong style={{ fontSize: 14 }}>{name}</Text>
          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
            {record.total_orders} orders · {record.total_challans} challans · {record.total_bills} bills
          </div>
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="% of challans delivered by PO due date. Excluded if PO had no delivery date.">
          On-Time <InfoCircleOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
        </Tooltip>
      ),
      dataIndex: 'on_time_delivery_rate',
      key: 'on_time',
      width: 110,
      align: 'center',
      render: (v) => <RateCell value={v} />,
      sorter: (a, b) => a.on_time_delivery_rate - b.on_time_delivery_rate,
    },
    {
      title: 'Qty Accuracy',
      dataIndex: 'quantity_accuracy_rate',
      key: 'qty_accuracy',
      width: 110,
      align: 'center',
      render: (v) => <RateCell value={v} />,
      sorter: (a, b) => a.quantity_accuracy_rate - b.quantity_accuracy_rate,
    },
    {
      title: (
        <Tooltip title="Lower is better — percentage of delivered quantity rejected on quality grounds.">
          Quality Rej. <InfoCircleOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
        </Tooltip>
      ),
      dataIndex: 'quality_rejection_rate',
      key: 'quality',
      width: 110,
      align: 'center',
      render: (v) => <RateCell value={v} lowerIsBetter />,
      sorter: (a, b) => a.quality_rejection_rate - b.quality_rejection_rate,
    },
    {
      title: 'Bill Accuracy',
      dataIndex: 'bill_accuracy_rate',
      key: 'bill_accuracy',
      width: 110,
      align: 'center',
      render: (v) => <RateCell value={v} />,
      sorter: (a, b) => a.bill_accuracy_rate - b.bill_accuracy_rate,
    },
    {
      title: 'Overall Score',
      dataIndex: 'overall_score',
      key: 'overall_score',
      width: 160,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.overall_score - b.overall_score,
      render: (v) => <ScoreBar value={v} />,
    },
    {
      title: '',
      key: 'action',
      width: 90,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          ghost
          icon={<TrophyOutlined />}
          onClick={() => setSelectedVendor(record)}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><TrophyOutlined /></div>
            <div className="page-header-text">
              <h1>Vendor Performance</h1>
              <p>Leaderboard computed from deliveries, quantities, and bill reconciliation</p>
            </div>
          </div>
        </div>

        {/* ── Summary Cards ─────────────────────────────────────────────── */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card size="small">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <TeamOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#1890ff' }}>{vendors.length}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Vendors Evaluated</Text>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card size="small">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <TrophyOutlined style={{ fontSize: 24, color: '#faad14' }} />
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: getScoreColor(avgScore) }}>
                    {Number(avgScore).toFixed(1)}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Average Score</Text>
                </div>
              </div>
            </Card>
          </Col>
          {topVendor && (
            <Col xs={24} sm={6}>
              <Card size="small" style={{ background: '#fffbe6', borderColor: '#ffe58f' }}>
                <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Top Performer</div>
                <Text strong style={{ fontSize: 14 }}>{topVendor.vendor_name}</Text>
                <div>
                  <Text style={{ color: '#52c41a', fontWeight: 600 }}>
                    {Number(topVendor.overall_score).toFixed(1)} / 100
                  </Text>
                  {' '}{getGradeTag(topVendor.score_grade)}
                </div>
              </Card>
            </Col>
          )}
          <Col xs={24} sm={6}>
            <Card size="small">
              <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 6 }}>Grade Distribution</div>
              <Space wrap size={4}>
                {['A', 'B', 'C', 'D', 'F'].map((g) =>
                  gradeCounts[g] ? (
                    <span key={g}>
                      {getGradeTag(g)}
                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 2 }}>{gradeCounts[g]}</Text>
                    </span>
                  ) : null
                )}
              </Space>
            </Card>
          </Col>
        </Row>

        {/* ── Filters ───────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <Space wrap>
            <RangePicker
              style={{ width: 260 }}
              format="DD MMM YYYY"
              value={dateRange}
              onChange={setDateRange}
              allowClear
              placeholder={['From Date', 'To Date']}
            />
            <Button type="primary" icon={<ReloadOutlined />} onClick={loadLeaderboard} loading={loading}>
              Refresh
            </Button>
            {dateRange && (
              <Button
                icon={<ClockCircleOutlined />}
                onClick={() => { setDateRange(null); }}
              >
                All Time
              </Button>
            )}
          </Space>
          {(leaderboard?.date_from || leaderboard?.date_to) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Showing data from {leaderboard.date_from || '—'} to {leaderboard.date_to || '—'}
            </Text>
          )}
        </div>

        {/* ── No-data alert ─────────────────────────────────────────────── */}
        {!loading && vendors.length === 0 && (
          <Alert
            type="info"
            showIcon
            message="No vendor data available"
            description="No vendors have purchase orders in the selected period. Try broadening the date range or check that orders have been recorded."
            style={{ marginBottom: 16 }}
          />
        )}

        {/* ── Leaderboard Table ─────────────────────────────────────────── */}
        <Table
          columns={columns}
          dataSource={vendors}
          rowKey="vendor_id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          scroll={{ x: 900 }}
          size="small"
          rowClassName={(_, i) => (i < 3 ? 'vendor-top-row' : '')}
          onRow={(record) => ({
            style: { cursor: 'pointer' },
            onClick: () => setSelectedVendor(record),
          })}
          locale={{
            emptyText: (
              <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
                No vendors to rank yet. Add purchase orders and delivery data first.
              </div>
            ),
          }}
        />

        {/* ── Formula note ──────────────────────────────────────────────── */}
        <div style={{ marginTop: 12, padding: '8px 12px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            <strong>Score formula:</strong> On-Time Delivery (35%) + Quantity Accuracy (25%) + Quality Score (20%) + Bill Accuracy (20%)
            · Vendors with zero orders in range are excluded.
          </Text>
        </div>
      </div>

      {/* ── Vendor Detail Modal ────────────────────────────────────────── */}
      {selectedVendor && (
        <VendorPerformanceDetail
          vendorId={selectedVendor.vendor_id}
          vendorName={selectedVendor.vendor_name}
          open={!!selectedVendor}
          onClose={() => setSelectedVendor(null)}
        />
      )}
    </div>
  );
};

export default VendorPerformance;
